import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useCurrency, useLocale } from "../../../intl";
import { formatPriceCop } from "../../../utils/automapListing/listingFormatters";
import {
  formatMonthlyPaymentEstimate,
  getVehiclePaymentEstimate,
  isDownPaymentMissing,
  vehiclePriceBucket,
} from "../../../utils/automap/buyerPaymentEstimate";
import {
  trackBuyerPaymentEstimateViewed,
  trackBuyerPaymentPreferencesEditClicked,
} from "../../../utils/automap/buyerPaymentEstimateAnalytics";
import { useLocalizedRuntimePaths } from "../../../clientViewRuntime/hooks/useLocalizedRuntimePaths";
import { useClientView } from "../../../clientViewRuntime/context/ClientViewProvider";
import BuyerPaymentQuickSetup from "./BuyerPaymentQuickSetup";
import "../../../styles/automapPaymentEstimate.css";

export default function VehiclePaymentEstimateCompact({
  listing,
  financingPrefs = {},
  hasSavedPrefs = false,
  isAuthenticated = false,
  onFinancingPrefsUpdated,
  source = "likes_card",
  showGuestQuickSetup = true,
  variant = "default",
}) {
  const { slug } = useClientView();
  const { t } = useLocale();
  const { formatMoney, displayCurrency: currency } = useCurrency();
  const { accountPath, signInPath } = useLocalizedRuntimePaths();
  const viewedRef = useRef(false);

  const priceCop = listing?.currentPriceCop ?? listing?.priceCop;
  const downPaymentMissing = isDownPaymentMissing(financingPrefs, slug);
  const estimate = useMemo(
    () =>
      downPaymentMissing
        ? null
        : getVehiclePaymentEstimate(priceCop, financingPrefs, { slug }),
    [priceCop, financingPrefs, slug, downPaymentMissing]
  );

  useEffect(() => {
    if (!estimate || !listing?.id || viewedRef.current) return;
    viewedRef.current = true;
    trackBuyerPaymentEstimateViewed(slug, {
      listingId: listing.id,
      source,
      hasSavedFinancingPrefs: hasSavedPrefs,
      termMonths: estimate.termMonths,
      vehiclePriceBucket: vehiclePriceBucket(estimate.vehiclePriceCop),
    });
  }, [estimate, listing?.id, slug, source, hasSavedPrefs]);

  const preferencesHref = `${accountPath}?section=preferences`;
  const isSidebar = variant === "sidebar";
  const rootClassName = [
    "automap-payment-estimate",
    "automap-payment-estimate--compact",
    isSidebar ? "automap-payment-estimate--sidebar" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!priceCop) {
    return (
      <div className={rootClassName}>
        <p className="muted automap-payment-estimate__unavailable">
          {t("automotive.paymentEstimate.unavailable")}
        </p>
      </div>
    );
  }

  if (downPaymentMissing) {
    if (!isAuthenticated) {
      return (
        <div className={`${rootClassName} automap-payment-estimate--pending`}>
          <p className="automap-payment-estimate__pending-title">
            {t("automotive.paymentEstimate.pendingTitle")}
          </p>
          <p className="muted automap-payment-estimate__hint">
            {t("automotive.paymentEstimate.guestPendingBody")}
          </p>
          <div className="automap-payment-estimate__auth-actions">
            <Link className="btn btn-primary automap-payment-estimate__sign-in" to={signInPath}>
              {t("automotive.account.signInCta")}
            </Link>
            {!showGuestQuickSetup ? (
              <Link className="btn btn-soft automap-payment-estimate__setup" to={accountPath}>
                {t("automotive.paymentEstimate.searchSetupCta")}
              </Link>
            ) : null}
          </div>
          {showGuestQuickSetup ? (
            <BuyerPaymentQuickSetup onSaved={onFinancingPrefsUpdated} />
          ) : (
            <p className="muted automap-payment-estimate__hint">
              {t("automotive.paymentEstimate.searchGuestHint")}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className={`${rootClassName} automap-payment-estimate--pending`}>
        <p className="automap-payment-estimate__pending-title">
          {t("automotive.paymentEstimate.pendingTitle")}
        </p>
        <p className="muted automap-payment-estimate__hint">
          {t("automotive.paymentEstimate.missingDownPaymentBody")}
        </p>
        <Link
          className="btn btn-soft automap-payment-estimate__edit-btn"
          to={preferencesHref}
          onClick={() =>
            trackBuyerPaymentPreferencesEditClicked(slug, {
              listingId: listing?.id,
              source,
            })
          }
        >
          {t("automotive.paymentEstimate.addDownPaymentCta")}
        </Link>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className={rootClassName}>
        <p className="muted automap-payment-estimate__unavailable">
          {t("automotive.paymentEstimate.unavailable")}
        </p>
      </div>
    );
  }

  const amountLabel = formatMonthlyPaymentEstimate(estimate, {
    formatMoney,
    currency,
    t,
  });
  const monthlyAmount = formatPriceCop(estimate.monthlyPaymentCop, currency, { formatMoney });
  const prefsHint = hasSavedPrefs
    ? t("automotive.paymentEstimate.basedOnSavedPrefs")
    : t("automotive.paymentEstimate.basedOnDefaults");

  const isSearchCard = source === "search_card";
  const editPreferencesLabel = isSearchCard
    ? t("automotive.paymentEstimate.editPreferencesShort")
    : t("automotive.paymentEstimate.editPreferences");

  const editPreferencesLink = (
    <Link
      className={
        isSidebar || isSearchCard
          ? "automap-payment-estimate__edit-btn automap-payment-estimate__edit-btn--subtle"
          : "btn btn-soft automap-payment-estimate__edit-btn"
      }
      to={preferencesHref}
      onClick={() =>
        trackBuyerPaymentPreferencesEditClicked(slug, {
          listingId: listing?.id,
          source,
        })
      }
    >
      {editPreferencesLabel}
    </Link>
  );

  if (isSidebar) {
    return (
      <div className={rootClassName}>
        <div className="automap-payment-estimate__hero" aria-label={amountLabel}>
          <p className="automap-payment-estimate__monthly-amount">{monthlyAmount}</p>
          <p className="automap-payment-estimate__monthly-period">
            {t("automotive.paymentEstimate.perMonth")}
          </p>
        </div>
        <p className="muted automap-payment-estimate__hint">{prefsHint}</p>
        {editPreferencesLink}
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      <p className="automap-payment-estimate__amount">
        {t("automotive.paymentEstimate.estPayment", { amount: amountLabel })}
      </p>
      <p className="muted automap-payment-estimate__hint">{prefsHint}</p>
      {editPreferencesLink}
    </div>
  );
}
